import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Calendar, Clock, MapPin, Users, Coffee, Phone, 
  Utensils, Plus, Check, X, HelpCircle, ChevronRight,
  Handshake, UserPlus, Briefcase
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Sidebar } from "./Dashboard";
import { useAuth, API } from "../App";
import { toast } from "sonner";

const Meetings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewMeeting, setShowNewMeeting] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [providers, setProviders] = useState([]);

  const fetchMeetings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/meetings`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setMeetings(response.data.meetings);
    } catch (error) {
      toast.error("Failed to load meetings");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchProviders = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/directory`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setProviders(response.data.physicians.filter(p => p.user_id !== user?.user_id));
    } catch (error) {
      toast.error("Unable to load providers");
    }
  }, [getAuthHeaders, user?.user_id]);

  useEffect(() => {
    fetchMeetings();
    fetchProviders();
  }, [fetchMeetings, fetchProviders]);

  const handleStatusUpdate = async (meetingId, status) => {
    try {
      await axios.put(`${API}/meetings/${meetingId}/status?status=${status}`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(`Meeting ${status}`);
      fetchMeetings();
      setSelectedMeeting(null);
    } catch (error) {
      toast.error("Failed to update meeting");
    }
  };

  const handleRequestAssistance = async (meetingId, assistanceType) => {
    try {
      await axios.post(`${API}/meetings/${meetingId}/request-assistance`, {
        assistance_type: assistanceType
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success("iConcierge has been notified!");
      fetchMeetings();
    } catch (error) {
      toast.error("Failed to request assistance");
    }
  };

  const getMeetingIcon = (type) => {
    const icons = {
      lunch: Utensils,
      breakfast: Coffee,
      coffee: Coffee,
      meeting: Users,
      call: Phone
    };
    return icons[type] || Users;
  };

  const getPurposeColor = (purpose) => {
    const colors = {
      referral: "bg-blue-50 text-blue-700 border-blue-200",
      partnership: "bg-purple-50 text-purple-700 border-purple-200",
      collaboration: "bg-emerald-50 text-emerald-700 border-emerald-200",
      introduction: "bg-amber-50 text-amber-700 border-amber-200"
    };
    return colors[purpose] || "bg-slate-50 text-slate-700";
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "badge-pending",
      confirmed: "badge-accepted",
      declined: "badge-declined",
      completed: "badge-completed",
      cancelled: "bg-slate-100 text-slate-600"
    };
    return styles[status] || "bg-slate-100";
  };

  const upcomingMeetings = meetings.filter(m => m.status === 'confirmed' || m.status === 'pending');
  const pastMeetings = meetings.filter(m => m.status === 'completed' || m.status === 'cancelled' || m.status === 'declined');

  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64">
      <Sidebar currentPath={location.pathname} />
      
      <main className="pt-20 lg:pt-8 p-4 lg:p-8" data-testid="meetings-main">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Meetings
              </h1>
              <p className="text-slate-600">Schedule and manage meetings with providers</p>
            </div>
            <Button 
              onClick={() => setShowNewMeeting(true)}
              className="bg-primary text-white hover:bg-primary/90 rounded-full"
              data-testid="new-meeting-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Schedule Meeting
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-5 h-5 text-primary" />
                <span className="text-sm text-slate-500">Upcoming</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {meetings.filter(m => m.status === 'confirmed').length}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-amber-500" />
                <span className="text-sm text-slate-500">Pending</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {meetings.filter(m => m.status === 'pending').length}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Check className="w-5 h-5 text-emerald-500" />
                <span className="text-sm text-slate-500">Completed</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {meetings.filter(m => m.status === 'completed').length}
              </p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <HelpCircle className="w-5 h-5 text-purple-500" />
                <span className="text-sm text-slate-500">Assistance</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">
                {meetings.filter(m => m.assistance_requested).length}
              </p>
            </div>
          </div>

          {/* Meetings List */}
          <Tabs defaultValue="upcoming">
            <TabsList className="bg-white border border-slate-200 p-1 mb-6">
              <TabsTrigger value="upcoming" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                Upcoming ({upcomingMeetings.length})
              </TabsTrigger>
              <TabsTrigger value="past" className="data-[state=active]:bg-primary data-[state=active]:text-white">
                Past ({pastMeetings.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="upcoming">
              {loading ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
                      <div className="h-4 bg-slate-200 rounded w-2/3 mb-4"></div>
                      <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : upcomingMeetings.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {upcomingMeetings.map((meeting) => {
                    const MeetingIcon = getMeetingIcon(meeting.meeting_type);
                    return (
                      <motion.div
                        key={meeting.meeting_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all cursor-pointer"
                        onClick={() => setSelectedMeeting(meeting)}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                              <MeetingIcon className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-900">{meeting.title}</h3>
                              <p className="text-sm text-slate-500">{meeting.meeting_type}</p>
                            </div>
                          </div>
                          <Badge className={getStatusBadge(meeting.status)}>
                            {meeting.status}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-3 mb-3">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={meeting.other_party?.picture} />
                            <AvatarFallback className="bg-primary/10 text-primary text-sm">
                              {meeting.other_party?.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-slate-700">{meeting.other_party?.name}</span>
                        </div>

                        <div className="space-y-2 text-sm text-slate-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            {meeting.date} at {meeting.time}
                          </div>
                          {meeting.location && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-slate-400" />
                              {meeting.location}
                            </div>
                          )}
                        </div>

                        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                          <Badge className={getPurposeColor(meeting.purpose)}>
                            {meeting.purpose}
                          </Badge>
                          {meeting.assistance_requested && (
                            <Badge className="bg-purple-100 text-purple-700">
                              <HelpCircle className="w-3 h-3 mr-1" />
                              Assistance Requested
                            </Badge>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No upcoming meetings</h3>
                  <p className="text-slate-600 mb-4">Schedule a meeting to connect with other providers</p>
                  <Button onClick={() => setShowNewMeeting(true)} className="bg-primary text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Schedule Meeting
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="past">
              {pastMeetings.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {pastMeetings.map((meeting) => {
                    const MeetingIcon = getMeetingIcon(meeting.meeting_type);
                    return (
                      <div
                        key={meeting.meeting_id}
                        className="bg-white rounded-xl border border-slate-200 p-5 opacity-75"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                              <MeetingIcon className="w-5 h-5 text-slate-500" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-slate-700">{meeting.title}</h3>
                              <p className="text-sm text-slate-500">{meeting.other_party?.name}</p>
                            </div>
                          </div>
                          <Badge className={getStatusBadge(meeting.status)}>
                            {meeting.status}
                          </Badge>
                        </div>
                        <div className="text-sm text-slate-500">
                          {meeting.date} at {meeting.time}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                  <Clock className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No past meetings</h3>
                  <p className="text-slate-600">Your completed meetings will appear here</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* New Meeting Modal */}
      <NewMeetingModal 
        open={showNewMeeting}
        onClose={() => setShowNewMeeting(false)}
        providers={providers}
        onSuccess={() => { fetchMeetings(); setShowNewMeeting(false); }}
      />

      {/* Meeting Detail Modal */}
      {selectedMeeting && (
        <MeetingDetailModal
          meeting={selectedMeeting}
          onClose={() => setSelectedMeeting(null)}
          onStatusUpdate={handleStatusUpdate}
          onRequestAssistance={handleRequestAssistance}
          isOrganizer={selectedMeeting.is_organizer}
        />
      )}
    </div>
  );
};

// New Meeting Modal
const NewMeetingModal = ({ open, onClose, providers, onSuccess }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    with_user_id: "",
    title: "",
    meeting_type: "coffee",
    date: "",
    time: "",
    location: "",
    notes: "",
    purpose: "referral"
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/meetings`, formData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success("Meeting scheduled!");
      onSuccess();
      setFormData({
        with_user_id: "",
        title: "",
        meeting_type: "coffee",
        date: "",
        time: "",
        location: "",
        notes: "",
        purpose: "referral"
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === "membership_required") {
        toast.error("Upgrade your membership to schedule meetings. Plans start at $29.99/mo.");
      } else {
        toast.error("Failed to schedule meeting");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Schedule a Meeting</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label>Meet With *</Label>
            <Select value={formData.with_user_id} onValueChange={(v) => setFormData({...formData, with_user_id: v})}>
              <SelectTrigger>
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                {providers.map((p) => (
                  <SelectItem key={p.user_id} value={p.user_id}>
                    {p.name} - {p.specialty || 'Provider'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="e.g., Discuss referral partnership"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type</Label>
              <Select value={formData.meeting_type} onValueChange={(v) => setFormData({...formData, meeting_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="coffee">Coffee</SelectItem>
                  <SelectItem value="lunch">Lunch</SelectItem>
                  <SelectItem value="breakfast">Breakfast</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="call">Phone Call</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Purpose</Label>
              <Select value={formData.purpose} onValueChange={(v) => setFormData({...formData, purpose: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="partnership">Partnership</SelectItem>
                  <SelectItem value="collaboration">Collaboration</SelectItem>
                  <SelectItem value="introduction">Introduction</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Time *</Label>
              <Input
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({...formData, time: e.target.value})}
                required
              />
            </div>
          </div>

          <div>
            <Label>Location</Label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              placeholder="Restaurant name or address"
            />
          </div>

          <div>
            <Label>Notes</Label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Any additional details..."
              className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-primary text-white"
              disabled={loading}
            >
              {loading ? "Scheduling..." : "Schedule Meeting"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Meeting Detail Modal
const MeetingDetailModal = ({ meeting, onClose, onStatusUpdate, onRequestAssistance, isOrganizer }) => {
  const MeetingIcon = {
    lunch: Utensils,
    breakfast: Coffee,
    coffee: Coffee,
    meeting: Users,
    call: Phone
  }[meeting.meeting_type] || Users;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <MeetingIcon className="w-5 h-5 text-primary" />
            </div>
            {meeting.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="flex items-center gap-3">
            <Avatar className="w-12 h-12">
              <AvatarImage src={meeting.other_party?.picture} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {meeting.other_party?.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-slate-900">{meeting.other_party?.name}</p>
              <p className="text-sm text-slate-500">{isOrganizer ? "You scheduled this" : "Invited you"}</p>
            </div>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-slate-400" />
              <span>{meeting.date} at {meeting.time}</span>
            </div>
            {meeting.location && (
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-slate-400" />
                <span>{meeting.location}</span>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Briefcase className="w-5 h-5 text-slate-400" />
              <span>Purpose: {meeting.purpose}</span>
            </div>
          </div>

          {meeting.notes && (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1">Notes</p>
              <p className="text-slate-600">{meeting.notes}</p>
            </div>
          )}

          {/* Action buttons */}
          {meeting.status === 'pending' && !isOrganizer && (
            <div className="flex gap-3 pt-4 border-t">
              <Button 
                variant="outline"
                className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => onStatusUpdate(meeting.meeting_id, 'declined')}
              >
                Decline
              </Button>
              <Button 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => onStatusUpdate(meeting.meeting_id, 'confirmed')}
              >
                Confirm
              </Button>
            </div>
          )}

          {meeting.status === 'confirmed' && (
            <>
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={() => onStatusUpdate(meeting.meeting_id, 'completed')}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Mark Complete
                </Button>
                <Button 
                  variant="outline"
                  className="flex-1 text-red-600 border-red-200"
                  onClick={() => onStatusUpdate(meeting.meeting_id, 'cancelled')}
                >
                  Cancel
                </Button>
              </div>

              {!meeting.assistance_requested && (
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-slate-700 mb-3">Need help from iConcierge?</p>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => onRequestAssistance(meeting.meeting_id, 'introduction')}
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Introduction
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => onRequestAssistance(meeting.meeting_id, 'marketing_description')}
                    >
                      <Briefcase className="w-4 h-4 mr-1" />
                      Marketing Help
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => onRequestAssistance(meeting.meeting_id, 'other')}
                    >
                      <HelpCircle className="w-4 h-4 mr-1" />
                      Other
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Meetings;
